import './memoryPostRotator.css';
import { useContext, useMemo, memo } from 'react';
import Post from '../post/Post';
import arePropsEqual from '../../arePropsEqual';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { CircularProgress } from '@material-ui/core';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from 'react-query';
import preciseFormat from '../../utils/preciseFormat';

const buildSeedKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMemoryDate = (createdAt) => {
  if (!createdAt) return '';
  try {
    return new Date(createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const MEMORY_POST_PROBABILITY = 0.38;

export default memo(function MemoryPostRotator() {
  const { player } = useContext(AuthContext);
  const mobileView = useMediaQuery({ query: '(max-width: 805px)' });
  const seedKey = useMemo(() => buildSeedKey(), []);
  const shouldFetchMemory = useMemo(
    () => Math.random() < MEMORY_POST_PROBABILITY,
    [],
  );

  const fetchMemoryPost = async () => {
    const res = await axiosConn.get(
      `posts/memory/${player.data.player._id}?seed=${seedKey}`,
      {
        headers: {
          Authorization: `Bearer ${player.token}`,
        },
      },
    );
    return res.data;
  };

  const { data, isLoading } = useQuery(
    ['memoryPost', player?.data?.player?._id, seedKey],
    fetchMemoryPost,
    {
      enabled: Boolean(player?.data?.player?._id) && shouldFetchMemory,
      staleTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
    },
  );

  const memoryPost = data?.post || null;
  const memoryContext = data?.memoryContext || {};
  const memoryMode = memoryContext?.mode || 'random';
  const memoryBadge =
    memoryMode === 'on-this-day' ? 'On this day' : 'Time capsule';
  const memoryAge = memoryPost?.createdAt
    ? preciseFormat(memoryPost.createdAt)
    : '';
  const memoryDate = formatMemoryDate(memoryPost?.createdAt);

  if (!shouldFetchMemory) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="feedPostProgress">
        <CircularProgress color="inherit" size="10px" />
      </div>
    );
  }

  if (!memoryPost) {
    return null;
  }

  return (
    <div className="memoryPostSection">
      <div className="memoryPostHeader">
        <div className="memoryPostBadge">
          <span className="memoryPostBadgeDot" />
          <span className="memoryPostBadgeText">{memoryBadge}</span>
        </div>
        <div className="memoryPostMeta">
          {memoryAge && <span className="memoryPostAge">{memoryAge}</span>}
          {memoryDate && (
            <span className="memoryPostDate">{memoryDate}</span>
          )}
        </div>
      </div>
      <div className={mobileView ? 'memoryPostCard mobile' : 'memoryPostCard'}>
        <Post key={memoryPost?._id} initialPost={memoryPost} />
      </div>
    </div>
  );
}, arePropsEqual);
